import React, { useState } from 'react';
import {
    DEFAULT_MANUAL_PAYMENT_METHOD,
    MANUAL_PAYMENT_TYPES,
    PAYMENT_METHODS,
    PAYMENT_TYPE_LABELS
} from '../constants/expensePayments';
import styles from './ExpensePaymentMatchModal.module.css';

const toAmount = value => Number.parseFloat(value) || 0;
const sameAmount = (left, right) => Math.abs(toAmount(left) - toAmount(right)) < 0.005;

/**
 * Add a payment that exists on the bank statement but in no reservation or
 * commission. It is invoiced like a reservation payment of the chosen type.
 */
export default function ExpenseManualPaymentForm({ defaults, candidates, onSubmit, onCancel }) {
    const [values, setValues] = useState({
        payment_type: MANUAL_PAYMENT_TYPES[0],
        client_name: '',
        amount: defaults.amount,
        payment_date: defaults.paymentDate,
        payment_method: DEFAULT_MANUAL_PAYMENT_METHOD,
        note: ''
    });
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState('');
    const set = (field) => (event) => setValues(current => ({ ...current, [field]: event.target.value }));

    const submit = async (event) => {
        event.preventDefault();
        setError('');
        if (!values.client_name.trim()) {
            setError('Le nom du client est obligatoire.');
            return;
        }
        if (!(toAmount(values.amount) > 0) || !values.payment_date) {
            setError('Montant et date de paiement obligatoires.');
            return;
        }

        // A recorded payment with the same amount and date is probably the one to match.
        const lookalike = candidates.find(row => row.source !== 'manual' && !row.matched_elsewhere &&
            row.payment_date_effective === values.payment_date && sameAmount(row.amount_effective, values.amount));
        if (lookalike && !window.confirm(
            `Un paiement de ${toAmount(lookalike.amount_effective).toFixed(2)}€ existe déjà ce jour-là (${lookalike.client_effective}). Ajouter quand même un paiement manuel ?`
        )) return;

        setSaving(true);
        try {
            await onSubmit(values);
        } catch (err) {
            setError(err.message || 'Erreur lors de l’ajout du paiement');
            setSaving(false);
        }
    };

    return (
        <form className={styles.manualForm} onSubmit={submit}>
            <div className={styles.manualFormTitle}>Paiement absent des réservations et commissions</div>
            <div className={styles.manualGrid}>
                <label>Type
                    <select className={styles.select} value={values.payment_type} onChange={set('payment_type')}>
                        {MANUAL_PAYMENT_TYPES.map(type => <option key={type} value={type}>{PAYMENT_TYPE_LABELS[type]}</option>)}
                    </select>
                </label>
                <label>Client
                    <input className={`${styles.input} ${styles.clientInput}`} value={values.client_name} onChange={set('client_name')} autoFocus required />
                </label>
                <label>Montant
                    <input className={styles.input} type="number" min="0.01" step="0.01" value={values.amount} onChange={set('amount')} required />
                </label>
                <label>Date paiement
                    <input className={`${styles.input} ${styles.dateInput}`} type="date" min="2000-01-01" max="2100-12-31" value={values.payment_date} onChange={set('payment_date')} required />
                </label>
                <label>Moyen
                    <select className={styles.select} value={values.payment_method} onChange={set('payment_method')}>
                        {PAYMENT_METHODS.map(method => <option key={method}>{method}</option>)}
                    </select>
                </label>
                <label className={styles.manualNote}>Note
                    <input className={`${styles.input} ${styles.manualNoteInput}`} value={values.note} onChange={set('note')} placeholder="Ex. client sans réservation" />
                </label>
            </div>
            <div className={styles.manualActions}>
                {error && <span className={styles.difference}>{error}</span>}
                <button type="button" className={`${styles.button} ${styles.cancel}`} onClick={onCancel} disabled={saving}>Annuler</button>
                <button type="submit" className={`${styles.button} ${styles.save}`} disabled={saving}>{saving ? 'Ajout...' : 'Ajouter et sélectionner'}</button>
            </div>
        </form>
    );
}
